import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { getRouteInconsistencies } from '../modules/routes/api';
import { getWebhookErrorCount } from '../modules/webhook-logs/api';
import { getAirtableErrorCount } from '../modules/airtable-logs/api';
import { getOpenTicketCount } from '../modules/support-tickets/api';
import { getIntakeRefusalCount } from '../modules/partner-intake/api';
import { getIntegrityViolationCount } from '../modules/integrity/api';
import { getCurrentImpersonation } from '../utils/impersonation';

/**
 * Polls the Monitoring issue counts (route inconsistencies over the last 7 days +
 * outstanding webhook errors + outstanding Airtable sync errors) so the menu can
 * show red badges. Cheap + fail-soft — any error just leaves the count at its
 * last value.
 *
 * #162 — every open tab polled 6 endpoints every minute (≈ 8–9 tabs in the
 * office, /routes/inconsistencies every ~7 s on one instance). A hidden tab
 * now skips its poll and refreshes when it becomes visible again; an
 * impersonated session never polls (the admin counters answer 403 there).
 */
export default function useMonitoringCounts(enabled = true, intervalMs = 60000) {
  const [counts, setCounts] = useState({
    inconsistencies: 0,
    webhookErrors: 0,
    airtableErrors: 0,
    openTickets: 0,
    // OVO-292 — upload items the planner refused and that still have no
    // live order (the BUT-1201 class: refused for a good reason, silently).
    intakeRefusals: 0,
    // #149 — open integrity violations (state machine + route steps).
    integrity: 0,
  });

  const load = useCallback(async () => {
    // +7d forward: overlapping SHIFTS matter most on future days (they only
    // produce ghost routes once the day is optimized) — the badge must fire
    // before that. Future routes add no inconsistency noise (no actuals yet).
    const to = dayjs().add(7, 'day').format('YYYY-MM-DD');
    const from = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
    const [inc, wh, at, tk, ir, ig] = await Promise.all([
      getRouteInconsistencies({ dateFrom: from, dateTo: to }).catch(() => ({ response: null })),
      getWebhookErrorCount().catch(() => ({ response: null })),
      getAirtableErrorCount().catch(() => ({ response: null })),
      getOpenTicketCount().catch(() => ({ response: null })),
      getIntakeRefusalCount().catch(() => ({ response: null })),
      getIntegrityViolationCount().catch(() => ({ response: null })),
    ]);
    setCounts({
      inconsistencies: (inc?.response?.inconsistencies?.length || 0) + (inc?.response?.shiftOverlaps?.length || 0),
      webhookErrors: wh?.response?.count || 0,
      airtableErrors: at?.response?.count || 0,
      openTickets: tk?.response?.count || 0,
      intakeRefusals: ir?.response?.count || 0,
      integrity: ig?.response?.count || 0,
    });
  }, []);

  const lastLoad = useRef(0);
  useEffect(() => {
    if (!enabled || getCurrentImpersonation()) return undefined;
    const tick = () => {
      if (document.hidden) return;
      lastLoad.current = Date.now();
      load();
    };
    const onVisible = () => {
      if (!document.hidden && Date.now() - lastLoad.current >= intervalMs) tick();
    };
    tick();
    const id = setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, intervalMs, load]);

  return counts;
}
